import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

// ─────────────────────────────────────────────────────────────
//  Panel admin: reservas llegadas desde campamentoaldealix.com (MotoPress).
//  Muestra su estado (pendiente de datos / completa / cancelada) para ver
//  de un vistazo a quién falta perseguir.
// ─────────────────────────────────────────────────────────────

const ESTADOS = {
  pending_details: { label: 'Pendiente de datos', color: '#e8a430', dot: '🟡' },
  completed:       { label: 'Completa',           color: '#4a7c59', dot: '🟢' },
  ya_registrada:   { label: 'Ya registrada',      color: '#4a8fa8', dot: '🔵' },
  cancelled:       { label: 'Cancelada',          color: '#d63c2a', dot: '🔴' },
};

const FILTROS = [
  { key: '',                label: 'Todas' },
  { key: 'pending_details', label: '🟡 Pendientes' },
  { key: 'completed',       label: '🟢 Completas' },
  { key: 'ya_registrada',   label: '🔵 Ya registradas' },
  { key: 'cancelled',       label: '🔴 Canceladas' },
];

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Firestore Timestamp serializado por JSON → { _seconds, _nanoseconds }; o ISO string.
function fmtRecibida(ts) {
  if (!ts) return '—';
  if (typeof ts === 'object' && ts._seconds) return new Date(ts._seconds * 1000).toLocaleString('es-ES');
  return new Date(ts).toLocaleString('es-ES');
}

function EstadoBadge({ estado }) {
  const e = ESTADOS[estado] || { label: estado, color: '#8fa89f', dot: '⚪' };
  return (
    <span className="badge" style={{ background: `${e.color}22`, color: e.color, border: `1px solid ${e.color}55` }}>
      {e.dot} {e.label}
    </span>
  );
}

// Etiqueta corta de la semana: "Semana 1" si el nombre lo contiene, si no el nombre.
function shortWeek(name) {
  const m = /Semana\s*\d+/i.exec(name || '');
  return m ? m[0] : (name || 'Semana');
}

function MemberCard({ p }) {
  return (
    <div className="member-box" style={{ marginBottom: '.5rem' }}>
      <div style={{ color: 'var(--white)', fontWeight: 600 }}>{p.name || `${p.firstName || ''} ${p.lastName1 || ''}`.trim() || '(sin nombre)'}</div>
      <div style={{ fontSize: '.8rem', color: 'var(--mid)', lineHeight: 1.6 }}>
        {p.birthYear ? `Nac. ${p.birthYear} · ` : ''}{p.sex || ''}
        {p.tutorName ? <div>Tutor: {p.tutorName} {p.tutorPhone ? `· ${p.tutorPhone}` : ''}</div> : null}
        {p.alergias ? <div>Alergias: {p.alergias}</div> : null}
        {p.medicacionActual ? <div>Medicación: {p.medicacionActual}</div> : null}
        {p.contactoEmergenciaNombre ? <div>Emergencia: {p.contactoEmergenciaNombre} {p.contactoEmergenciaTelefono || ''}</div> : null}
        <div style={{ marginTop: '.2rem' }}>
          {p.autorizaCesionDatos ? '✅ Cesión datos' : '❌ Cesión datos'}
          {p.autorizaImagenRedes ? ' · ✅ Imagen' : ' · ❌ Imagen'}
          {p.autorizaMedicacion ? ' · ✅ Medicación' : ''}
        </div>
      </div>
    </div>
  );
}

function WeekChips({ events }) {
  if (!events || events.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap' }}>
      {events.map(ev => (
        <span key={ev.id} className="badge" style={{ background: '#4a8fa822', color: '#7ab0c8', border: '1px solid #4a8fa855', fontSize: '.72rem' }}>
          📅 {shortWeek(ev.name)}
        </span>
      ))}
    </span>
  );
}

// Modal para crear a mano una reserva de transferencia/offline.
function ManualReservaModal({ token, eventos, onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [eventIds, setEventIds] = useState([]);
  const [plazas, setPlazas] = useState(1);
  const [total, setTotal] = useState('');
  const [creando, setCreando] = useState(false);
  const [err, setErr] = useState('');

  function toggleEvento(id) {
    setEventIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function handleCrear(e) {
    e.preventDefault();
    setErr('');
    const emailLimpio = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailLimpio)) {
      setErr('Introduce un email válido.');
      return;
    }
    if (eventIds.length === 0) {
      setErr('Selecciona al menos una semana.');
      return;
    }
    setCreando(true);
    try {
      await api.createMotopressManual(token, {
        email: emailLimpio,
        customerName: customerName.trim(),
        eventIds,
        guests: Number(plazas),
        total: Number(total),
      });
      await onCreated();
    } catch (e2) {
      setErr(e2.message || 'No se pudo crear la reserva.');
    } finally {
      setCreando(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="section-label">Nueva reserva manual</div>
        <h2 style={{ fontSize: '1.15rem', color: 'var(--white)', margin: '0 0 .9rem' }}>Reserva de transferencia / offline</h2>

        <form onSubmit={handleCrear}>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              className="form-input"
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="familia@correo.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Nombre del cliente</label>
            <input
              className="form-input"
              type="text"
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
              placeholder="Nombre y apellidos"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Semana(s) *</label>
            {eventos.length === 0 ? (
              <div style={{ fontSize: '.85rem', color: 'var(--mid)' }}>No hay semanas disponibles.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                {eventos.map(ev => (
                  <label key={ev.id} className="member-box" style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', padding: '.5rem .7rem' }}>
                    <input
                      type="checkbox"
                      checked={eventIds.includes(ev.id)}
                      onChange={() => toggleEvento(ev.id)}
                    />
                    <span style={{ color: 'var(--white)', fontSize: '.88rem' }}>📅 {ev.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Nº de plazas</label>
            <input
              className="form-input"
              type="number"
              min="1"
              value={plazas}
              onChange={e => setPlazas(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Total (€)</label>
            <input
              className="form-input"
              type="number"
              min="0"
              step="0.01"
              value={total}
              onChange={e => setTotal(e.target.value)}
              placeholder="0"
            />
          </div>

          {err && <div className="alert alert-error"><span>⚠</span> {err}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem', marginTop: '1rem' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={creando}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={creando}>
              {creando ? <><span className="spinner" /> Creando…</> : 'Crear reserva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminCampamento() {
  const { token } = useAuth();
  const [reservas, setReservas] = useState([]);
  const [resumen, setResumen] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [semana, setSemana] = useState(''); // filtro por evento-semana (id)
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [eventos, setEventos] = useState([]); // eventos-semana para el selector de reserva manual
  const [sending, setSending] = useState(null); // id de la reserva cuyo email se está enviando
  const [enviandoPago, setEnviandoPago] = useState(false); // aviso de pago pendiente en bloque

  // Semanas disponibles (a partir de las reservas cargadas) para el filtro.
  const semanasDisponibles = (() => {
    const map = new Map();
    for (const r of reservas) for (const ev of (r.matchedEvents || [])) if (!map.has(ev.id)) map.set(ev.id, ev);
    return [...map.values()].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  })();
  const visibles = semana ? reservas.filter(r => (r.matchedEvents || []).some(ev => ev.id === semana)) : reservas;

  const load = useCallback(async (estado) => {
    setLoading(true); setError('');
    try {
      const r = await api.getAdminMotopressReservas(token, estado || undefined);
      setReservas(r.reservas || []);
      setResumen(r.resumen || {});
    } catch (e) {
      setError(e.message || 'No se pudieron cargar las reservas.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(filtro); }, [load, filtro]);

  // Eventos-semana de campamento para el selector de la reserva manual.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.getAdminEvents(token);
        const lista = r.events || r || [];
        setEventos(
          lista
            .filter(ev => /semana/i.test(ev.name || ''))
            .map(ev => ({ id: ev.id, name: ev.name }))
        );
      } catch {
        /* el selector simplemente quedará vacío */
      }
    })();
  }, [token]);

  async function handlePago(r) {
    const actual = r.reserva?.paid != null ? r.reserva.paid : '';
    const total = r.reserva?.total || 0;
    const input = window.prompt(
      `Importe ya pagado por la familia (€) — p.ej. la transferencia.\n` +
      `Total de la reserva: ${total}€. Pon ${total} si han pagado todo por transferencia.`,
      actual);
    if (input === null) return;
    const paid = Number(String(input).replace(',', '.'));
    if (!Number.isFinite(paid) || paid < 0) { alert('Importe no válido.'); return; }
    try {
      const res = await api.updateMotopressPago(token, r.id, { paid });
      setReservas(prev => prev.map(x => x.id === r.id
        ? { ...x, metodoPago: 'transferencia', reserva: { ...x.reserva, paid } }
        : x));
      alert(`Registrado. Resto pendiente: ${res.remaining}€.`);
    } catch (e) {
      alert(e.message || 'No se pudo registrar el pago.');
    }
  }

  async function handleEditarTotal(r) {
    const actual = r.reserva?.total != null ? r.reserva.total : '';
    const input = window.prompt(
      `Precio total de la reserva (€).\n` +
      `El "resto pendiente" se recalcula como total − pagado.`,
      actual);
    if (input === null) return;
    const total = Number(String(input).replace(',', '.'));
    if (!Number.isFinite(total) || total < 0) { alert('Importe no válido.'); return; }
    try {
      const res = await api.updateMotopressPago(token, r.id, { total });
      setReservas(prev => prev.map(x => x.id === r.id
        ? { ...x, reserva: { ...x.reserva, total } }
        : x));
      alert(`Total actualizado. Resto pendiente: ${res.remaining}€.`);
    } catch (e) {
      alert(e.message || 'No se pudo actualizar el total.');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar esta reserva del panel? (No afecta a MotoPress)')) return;
    try {
      await api.deleteAdminMotopressReserva(token, id);
      setReservas(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      alert(e.message || 'No se pudo eliminar.');
    }
  }

  // Envía el email (completar datos / pago pendiente) a una sola reserva.
  async function handleSendEmail(r) {
    if (!window.confirm(`¿Enviar el email a ${r.email}?`)) return;
    setSending(r.id);
    try {
      await api.sendMotopressEmail(token, r.id);
      setReservas(prev => prev.map(x => x.id === r.id
        ? { ...x, emailCompletarEnviadoEn: new Date().toISOString() }
        : x));
      alert(`Email enviado a ${r.email}.`);
    } catch (e) {
      alert(e.message || 'No se pudo enviar el email.');
    } finally {
      setSending(null);
    }
  }

  // Avisa del pago pendiente, en bloque, a los registrados que no han pagado.
  async function handleEmailPagoPendiente() {
    const pendientes = reservas.filter(r => {
      const total = Number(r.reserva?.total) || 0;
      const paid = Number(r.reserva?.paid) || 0;
      const pagadoDelTodo = r.pagoCompletado || (total > 0 && paid >= total);
      if (!(['pending_details', 'completed'].includes(r.estado) && !pagadoDelTodo)) return false;
      if (semana) return (r.matchedEvents || []).some(ev => ev.id === semana);
      return true;
    });
    if (pendientes.length === 0) {
      alert('No hay reservas pendientes de pago para avisar.');
      return;
    }
    if (!window.confirm(`¿Avisar a las ${pendientes.length} reserva(s) que aún no han pagado el resto? (incluye las que todavía no han rellenado datos)`)) return;
    setEnviandoPago(true);
    try {
      const res = await api.sendMotopressEmailPagoPendiente(token, semana || undefined);
      alert(`Enviados ${res.enviados} de ${res.total}.`);
      await load(filtro);
    } catch (e) {
      alert(e.message || 'No se pudo enviar el aviso de pago.');
    } finally {
      setEnviandoPago(false);
    }
  }

  return (
    <div className="main-content page-enter">
      {/* Cabecera */}
      <div style={{ marginBottom: '1rem' }}>
        <div className="section-label">Campamento web</div>
        <h1 style={{ fontSize: '1.4rem', color: 'var(--white)', margin: 0 }}>Reservas de campamentoaldealix.com</h1>
        <p style={{ color: 'var(--mid)', fontSize: '.85rem', marginTop: '.3rem' }}>
          Reservas llegadas desde la web. Las 🟡 pendientes aún no han rellenado los datos de los campistas.
        </p>
      </div>

      {/* Sección: Acciones */}
      <div className="card" style={{ padding: '.8rem 1rem', marginBottom: '1rem' }}>
        <div className="section-label" style={{ marginBottom: '.55rem' }}>Acciones</div>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={() => setShowManual(true)}>➕ Añadir reserva manual</button>
          <button className="btn btn-ghost btn-sm" onClick={handleEmailPagoPendiente} disabled={enviandoPago}>
            {enviandoPago ? <><span className="spinner" /> Enviando…</> : '✉️ Avisar pago pendiente'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => load(filtro)} disabled={loading}>↻ Actualizar</button>
        </div>
      </div>

      {showManual && (
        <ManualReservaModal
          token={token}
          eventos={eventos}
          onClose={() => setShowManual(false)}
          onCreated={async () => {
            setShowManual(false);
            await load(filtro);
            alert("Reserva manual creada. Envía el enlace para completar datos/pago desde el Roster semanal.");
          }}
        />
      )}

      {/* Sección: Resumen */}
      <div style={{ marginBottom: '1rem' }}>
        <div className="section-label" style={{ marginBottom: '.55rem' }}>Resumen</div>
        <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap' }}>
          {Object.entries(ESTADOS).map(([key, e]) => (
            <div key={key} className="card" style={{ padding: '.7rem 1rem', minWidth: 130 }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: e.color }}>{resumen[key] || 0}</div>
              <div style={{ fontSize: '.78rem', color: 'var(--mid)' }}>{e.dot} {e.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Sección: Filtros (estado + semana) */}
      <div className="card" style={{ padding: '.8rem 1rem', marginBottom: '1rem' }}>
        <div className="section-label" style={{ marginBottom: '.55rem' }}>Filtros</div>
        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: semanasDisponibles.length > 0 ? '.6rem' : 0 }}>
          <span style={{ fontSize: '.78rem', color: 'var(--mid)', minWidth: 58 }}>Estado:</span>
          {FILTROS.map(f => (
            <button key={f.key}
              className={`btn btn-sm ${filtro === f.key ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFiltro(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        {semanasDisponibles.length > 0 && (
          <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '.78rem', color: 'var(--mid)', minWidth: 58 }}>Semana:</span>
            <button className={`btn btn-sm ${semana === '' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setSemana('')}>Todas</button>
            {semanasDisponibles.map(ev => (
              <button key={ev.id}
                className={`btn btn-sm ${semana === ev.id ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setSemana(ev.id)}>
                📅 {shortWeek(ev.name)}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}><span className="spinner spinner-lg" /></div>
      ) : visibles.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--mid)' }}>
          No hay reservas {filtro || semana ? 'con este filtro' : 'todavía'}.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
          {visibles.map(r => {
            const isOpen = expanded === r.id;
            const reserva = r.reserva || {};
            return (
              <div key={r.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '.9rem 1rem', cursor: 'pointer', flexWrap: 'wrap' }}
                  onClick={() => setExpanded(isOpen ? null : r.id)}>
                  <EstadoBadge estado={r.estado} />
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ color: 'var(--white)', fontWeight: 600 }}>
                      {reserva.customerName || '(sin nombre)'} <span style={{ color: 'var(--mid)', fontWeight: 400, fontSize: '.85rem' }}>· #{r.externalId}</span>
                    </div>
                    <div style={{ color: 'var(--mid)', fontSize: '.82rem' }}>{r.email}</div>
                    <div style={{ marginTop: '.3rem' }}><WeekChips events={r.matchedEvents} /></div>
                  </div>
                  <div style={{ color: 'var(--mid)', fontSize: '.82rem', textAlign: 'right' }}>
                    <div>{fmtDate(reserva.checkInDate)} → {fmtDate(reserva.checkOutDate)}</div>
                    <div>{reserva.guests || 1} plaza(s)</div>
                  </div>
                  <span style={{ color: 'var(--mid)' }}>{isOpen ? '▲' : '▼'}</span>
                </div>

                {isOpen && (
                  <div style={{ padding: '0 1rem 1rem', borderTop: '1px solid var(--green-border)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.4rem 1.5rem', fontSize: '.85rem', color: 'var(--mid)', marginTop: '.8rem' }}>
                      <div>📞 {reserva.customerPhone || '—'}</div>
                      <div>🏷️ {reserva.accommodationType || '—'}</div>
                      <div>📥 Recibida: {fmtRecibida(r.webhookRecibidoEn || r.createdAt)}</div>
                      <div>💶 Total: {reserva.total != null ? `${reserva.total} ${reserva.currency || 'EUR'}` : '—'}{reserva.paid ? ` · pagado ${reserva.paid}€${r.metodoPago === 'transferencia' ? ' (transf.)' : ''}` : ''}</div>
                      {r.busCount > 0 && <div>🚌 Bus: {r.busCount} campista(s) · {r.busTotal}€{r.busPagado ? <span style={{ color: 'var(--green-light)' }}> · pagado ✅</span> : ''}</div>}
                      <div>{(() => {
                        const total = Number(reserva.total) || 0;
                        const paid = Number(reserva.paid) || 0;
                        const fullyTransfer = total > 0 && paid >= total;
                        if (r.pagoCompletado) return <span style={{ color: 'var(--green-light)' }}>💳 Resto pagado{r.pagadoEnReservas ? ` (${r.pagadoEnReservas}€)` : ''} ✅</span>;
                        if (fullyTransfer) return <span style={{ color: 'var(--green-light)' }}>💳 Pagado por transferencia ✅</span>;
                        if (r.estado === 'ya_registrada') return <span>💳 Pago gestionado en la reserva nativa</span>;
                        return <span style={{ color: 'var(--warn)' }}>💳 Resto pendiente de pago</span>;
                      })()}</div>
                    </div>

                    {r.yaRegistrada && (
                      <div style={{ marginTop: '.8rem', background: '#4a8fa818', border: '1px solid #4a8fa855', borderRadius: 8, padding: '.7rem .9rem', fontSize: '.85rem', color: 'var(--white)' }}>
                        🔵 Esta reserva <strong>ya está inscrita</strong> en reservas.aldealix.org (mismo email y semana). No se ha sumado al aforo de nuevo ni hace falta que complete datos.
                      </div>
                    )}

                    {r.matchedEvents?.length > 0 && (
                      <div style={{ marginTop: '.8rem' }}>
                        <div className="section-label">Semanas reservadas</div>
                        <div style={{ fontSize: '.85rem', color: 'var(--white)', lineHeight: 1.7 }}>
                          {r.matchedEvents.map(ev => (
                            <div key={ev.id}>📅 {ev.name} <span style={{ color: 'var(--mid)' }}>({ev.date} → {ev.dateEnd})</span></div>
                          ))}
                          <div style={{ color: 'var(--mid)', fontSize: '.78rem', marginTop: '.2rem' }}>
                            {reserva.guests} plaza(s) sumadas al aforo de cada semana.
                          </div>
                        </div>
                      </div>
                    )}

                    {reserva.customerNote && (
                      <div style={{ marginTop: '.8rem' }}>
                        <div className="section-label">Nota del cliente</div>
                        <div style={{ fontSize: '.85rem', color: 'var(--white)', whiteSpace: 'pre-wrap' }}>{reserva.customerNote}</div>
                      </div>
                    )}

                    {/* Campistas: completa→personas; ya_registrada→datos de la nativa; pendiente→huéspedes MotoPress */}
                    {(() => {
                      const natMembers = (r.nativeBookings || []).flatMap(nb => nb.members || []);
                      const usarPersonas = r.estado === 'completed' && r.personas?.length;
                      const usarNativa = r.estado === 'ya_registrada' && natMembers.length;
                      const lista = usarPersonas ? r.personas : (usarNativa ? natMembers : null);
                      const count = lista ? lista.length : (reserva.reservedRooms?.length || 0);
                      return (
                        <div style={{ marginTop: '.8rem' }}>
                          <div className="section-label">Campistas ({count})</div>
                          {usarNativa && (
                            <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginBottom: '.3rem' }}>
                              Datos tomados de la inscripción en reservas.aldealix.org
                            </div>
                          )}
                          {lista ? (
                            lista.map((p, i) => <MemberCard key={i} p={p} />)
                          ) : (
                            <div style={{ fontSize: '.85rem', color: 'var(--mid)' }}>
                              {(reserva.reservedRooms || []).map((rm, i) => (
                                <div key={i}>🧒 {rm.guestName || '(huésped sin nombre)'} <span style={{ opacity: .7 }}>· {rm.accommodationType}</span></div>
                              ))}
                              {r.estado === 'pending_details' && (
                                <div style={{ color: 'var(--warn)', marginTop: '.4rem' }}>⏳ Aún no ha rellenado los datos en reservas.aldealix.org</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <div style={{ marginTop: '.9rem', borderTop: '1px solid var(--green-border)', paddingTop: '.7rem' }}>
                      {r.emailCompletarEnviadoEn && (
                        <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginBottom: '.55rem' }}>✉️ Email enviado: {fmtRecibida(r.emailCompletarEnviadoEn)}</div>
                      )}
                      <div style={{ display: 'flex', gap: '1.4rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        {(() => {
                          const pagadoDelTodo = r.pagoCompletado || ((Number(r.reserva?.total) || 0) > 0 && (Number(r.reserva?.paid) || 0) >= (Number(r.reserva?.total) || 0));
                          return r.estado !== 'cancelled' && !pagadoDelTodo;
                        })() && (
                          <div>
                            <div className="section-label" style={{ marginBottom: '.35rem' }}>Comunicación</div>
                            <button className="btn btn-primary btn-sm" onClick={() => handleSendEmail(r)} disabled={sending === r.id}>
                              {sending === r.id ? <><span className="spinner" /> Enviando…</> : '✉️ Enviar email'}
                            </button>
                          </div>
                        )}
                        {r.estado !== 'ya_registrada' && (
                          <div>
                            <div className="section-label" style={{ marginBottom: '.35rem' }}>Pago</div>
                            <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => handlePago(r)}>💶 Registrar transferencia</button>
                              <button className="btn btn-ghost btn-sm" onClick={() => handleEditarTotal(r)}>✏️ Editar precio total</button>
                            </div>
                          </div>
                        )}
                        <div>
                          <div className="section-label" style={{ marginBottom: '.35rem' }}>Gestión</div>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(r.id)}>🗑 Eliminar del panel</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
